import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import { CreateElement } from 'vue/types/umd';
import { SortOrder } from './enums/SortOrder';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,

  /*
   * | ------------------------
   * | Simulate native-like scroll behavior when navigating to a new
   * | route and using back/forward buttons.
   * | ------------------------
   * |
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },

  /*
   * | ------------------------
   * | All router page routes
   * | Contains all routes and their matching components
   * | ------------------------
   * |
   */
  routes: [
    {
      path: '/',
      name: 'home',
      beforeEnter: guardAuth,
      meta: {
        group: ['guest', 'auth'],
      },
    },
    {
      path: '/register',
      name: 'register',
      beforeEnter: guardAuth,
      component: loadView('guest/register'),
      meta: {
        title: 'Registreer | Induline product manager',
        group: ['guest'],
      },
    },
    {
      path: '/login',
      name: 'login',
      beforeEnter: guardAuth,
      component: loadView('guest/login'),
      meta: {
        title: 'Login | Induline product manager',
        group: ['guest'],
      },
    },
    {
      path: '/logout',
      name: 'logout',
      beforeEnter: guardAuth,
      meta: {
        title: 'logout | Induline product manager',
        group: ['logout'],
      },
    },

    /*
        | -----------------
        | PRODUCTS
        | -----------------
        | Show and manage all products, variants and product sections.
        */
    {
      path: '/products',
      name: 'products',
      beforeEnter: guardAuth,
      component: loadView('auth/products/index'),
      meta: {
        title: 'Products | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          const workspaceId = store.getters['workspace/selected'].id;

          store
            .dispatch('product/index', { query: { per_page: 20, s: { created_at: SortOrder.DESC, id: SortOrder.ASC } }, workspaceId })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },
    {
      path: '/products/create',
      name: 'products-create',
      beforeEnter: guardAuth,
      component: loadView('auth/products/show'),
      meta: {
        title: 'Products create | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('product/clearViewing');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
    {
      path: '/products/:id',
      name: 'products-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/products/show'),
      meta: {
        title: 'Products | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('product/read', { ...to.params, workspaceId })
            .then((product: Product) => {
              return store.dispatch('collect/index', {
                query: { q: { product_id: product.id }, per_page: product.collections_total, s: { ordinal: SortOrder.ASC, created_at: SortOrder.DESC, id: SortOrder.ASC } },
                workspaceId,
              });
            })
            .then(() => {
              next();
            })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },
    {
      path: '/products/:id/sections',
      name: 'products-sections',
      beforeEnter: guardAuth,
      component: loadView('auth/products/sections'),
      meta: {
        title: 'Products | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store.dispatch('section/index', { productId: to.params.id, workspaceId }).finally(() => {
            next();
            store.dispatch('stopLoadingPage');
          });
        },
      },
    },

    /*
        | -----------------
        | COLLECTIONS
        | -----------------
        | Collections are a group of products that can be manually selected.
        */
    {
      path: '/collections',
      name: 'collections',
      beforeEnter: guardAuth,
      component: loadView('auth/collections/index'),
      meta: {
        title: 'Collections | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;

          store.dispatch('startLoadingPage');

          next();

          store
            .dispatch('collection/index', { workspaceId })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },
    {
      path: '/collections/create',
      name: 'collections-create',
      beforeEnter: guardAuth,
      component: loadView('auth/collections/show'),
      meta: {
        title: 'Collections | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('collection/clearViewing');
          store.dispatch('collect/reset');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
    {
      path: '/collections/:id',
      name: 'collections-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/collections/show'),
      meta: {
        title: 'Collections | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;

          store.dispatch('startLoadingPage');

          store
            .dispatch('collection/read', { ...to.params, workspaceId })
            .then((collection: Collection) => {
              return store.dispatch('collect/index', {
                query: { q: { collection_id: collection.id }, per_page: collection.products_total, s: { ordinal: SortOrder.ASC, created_at: SortOrder.DESC, id: SortOrder.ASC } },
                workspaceId,
              });
            })
            .then(() => {
              next();
            })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },

    /*
        | -----------------
        | RESELLERS
        | -----------------
        | Shows a list of all resellers on the platform
        | Shows the reseller detail page
        */
    {
      path: '/resellers',
      name: 'resellers',
      beforeEnter: guardAuth,
      component: loadView('auth/resellers/index'),
      meta: {
        title: 'Resellers | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store
            .dispatch('reseller/index')
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },
    {
      path: '/resellers/create',
      name: 'resellers-create',
      beforeEnter: guardAuth,
      component: loadView('auth/resellers/show'),
      meta: {
        title: 'Resellers | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('reseller/clearViewing');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
    {
      path: '/resellers/:id',
      name: 'resellers-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/resellers/show'),
      meta: {
        title: 'Resellers | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store
            .dispatch('reseller/read', to.params)
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },

    /*
        | -----------------
        | ACCOUNTS
        | -----------------
        | Shows a list of all accounts on the platform
        | Shows the accounts detail page
        */
    {
      path: '/accounts',
      name: 'accounts',
      beforeEnter: guardAuth,
      component: loadView('auth/accounts/index'),
      meta: {
        title: 'Accounts | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('invite/index');
          store
            .dispatch('account/index')
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },
    {
      path: '/accounts/invite',
      name: 'accounts-invite',
      beforeEnter: guardAuth,
      component: loadView('auth/accounts/invite'),
      meta: {
        title: 'Accounts invite | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;

          store.dispatch('startLoadingPage');

          store
            .dispatch('role/index', { query: { q: { not: { name: 'api_token' } } }, workspaceId })
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },
    {
      path: '/accounts/:id',
      name: 'accounts-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/accounts/show'),
      meta: {
        title: 'Accounts | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store
            .dispatch('account/read', to.params)
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },

    /*
        | -----------------
        | CUSTOMERS
        | -----------------
        | Show and manage platform customer
        */
    {
      path: '/customers',
      name: 'customers',
      beforeEnter: guardAuth,
      component: loadView('auth/customers/index'),
      meta: {
        title: 'Customers | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },

    /*
        | -----------------
        | ROLES
        | -----------------
        | Show and manage all platform roles and permissions
        */
    {
      path: '/roles',
      name: 'roles',
      beforeEnter: guardAuth,
      component: loadView('auth/roles/index'),
      meta: {
        title: 'Roles | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('role/index').finally(() => {
            store.dispatch('stopLoadingPage');
          });
        },
      },
    },

    /*
        | -----------------
        | REVIEWS
        | -----------------
        | Show and manage all platform reviews
        */
    {
      path: '/reviews',
      name: 'reviews',
      beforeEnter: guardAuth,
      component: loadView('auth/reviews/index'),
      meta: {
        title: 'Reviews | Induline product manager',
        group: ['auth'],
        navGroup: ['users'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('review/index').finally(() => {
            store.dispatch('stopLoadingPage');
          });
        },
      },
    },

    /*
        | -----------------
        | SHIPPING
        | -----------------
        | Show and manage all platform shipping methods
        */
    {
      path: '/shipping-methods',
      name: 'shipping-methods',
      beforeEnter: guardAuth,
      component: loadView('auth/shipping/index'),
      meta: {
        title: 'Shipping methhods | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('shipping/index').finally(() => {
            store.dispatch('stopLoadingPage');
          });
        },
      },
    },

    /*
        | -----------------
        | DASHBOARD
        | -----------------
        | Show platform analytics and statistics
        */
    {
      path: '/dashboard',
      name: 'dashboard',
      beforeEnter: guardAuth,
      component: loadView('auth/dashboard/home'),
      meta: {
        title: 'Dashboard | Induline product manager',
        group: ['auth'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },

    /*
        | -----------------
        | ORDERS
        | -----------------
        | Show and manage platform orders
        */
    {
      path: '/orders',
      name: 'orders',
      beforeEnter: guardAuth,
      component: loadView('auth/orders/index'),
      meta: {
        title: 'Orders | Induline product manager',
        group: ['auth'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;

          store.dispatch('startLoadingPage');

          next();

          store
            .dispatch('order/index', { query: { per_page: 20, s: { created_at: SortOrder.DESC, id: SortOrder.ASC } }, workspaceId })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },

    {
      path: '/orders/:id',
      name: 'orders-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/orders/show'),
      meta: {
        title: 'Orders | Induline product manager',
        group: ['auth'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          next();

          store
            .dispatch('order/read', { ...to.params, workspaceId })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },

    /*
        | -----------------
        | LOGS
        | -----------------
        | Show a list of all platform cafka logs
        */
    {
      path: '/logs',
      name: 'logs',
      beforeEnter: guardAuth,
      component: loadView('auth/logs/index'),
      meta: {
        title: 'Logs | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },

    /*
        | -----------------
        | OPTIONS
        | -----------------
        | Show and manage options
        */
    {
      path: '/options',
      name: 'options',
      beforeEnter: guardAuth,
      component: loadView('auth/options/index'),
      meta: {
        title: 'Options | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('option/index', { query: { s: { id: SortOrder.ASC }, per_page: 20 }, workspaceId })
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },
    {
      path: '/options/create',
      name: 'options-create',
      beforeEnter: guardAuth,
      component: loadView('auth/options/show'),
      meta: {
        title: 'Options create | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('option/clearViewing');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
    {
      path: '/options/:id',
      name: 'options-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/options/show'),
      meta: {
        title: 'Options | Induline product manager',
        group: ['auth'],
        navGroup: ['products'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('option/read', { ...to.params, workspaceId })
            .then(() => {
              next();
            })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },
    {
      path: '/diesel-taxes',
      name: 'diesel-taxes',
      beforeEnter: guardAuth,
      component: loadView('auth/diesel-tax/index'),
      meta: {
        title: 'Dieseltoeslag | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('diesel-tax/index', { query: { s: { country: SortOrder.ASC, id: SortOrder.ASC }, per_page: 20 }, workspaceId })
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },
    {
      path: '/diesel-taxes/create',
      name: 'diesel-taxes-create',
      beforeEnter: guardAuth,
      component: loadView('auth/diesel-tax/show'),
      meta: {
        title: 'Dieseltoeslag toevoegen | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('diesel-tax/clearViewing');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
    {
      path: '/diesel-taxes/:id',
      name: 'diesel-taxes-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/diesel-tax/show'),
      meta: {
        title: 'Dieseltoeslag | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('diesel-tax/read', { ...to.params, workspaceId })
            .then(() => {
              next();
            })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },

    {
      path: '/transport-costs',
      name: 'transport-costs',
      beforeEnter: guardAuth,
      component: loadView('auth/transport-cost/index'),
      meta: {
        title: 'Transportkosten | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('transport-costs/index', { query: { s: { type: SortOrder.ASC, id: SortOrder.ASC }, per_page: 20 }, workspaceId })
            .then(() => next())
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => store.dispatch('stopLoadingPage'));
        },
      },
    },
    {
      path: '/transport-costs/create',
      name: 'transport-costs-create',
      beforeEnter: guardAuth,
      component: loadView('auth/transport-cost/show'),
      meta: {
        title: 'Transportkost toevoegen | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          store.dispatch('startLoadingPage');

          store.dispatch('transport-costs/clearViewing');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
    {
      path: '/transport-costs/:id',
      name: 'transport-costs-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/transport-cost/show'),
      meta: {
        title: 'Transportkosten | Induline product manager',
        group: ['auth'],
        navGroup: ['settings'],
        async beforeResolve(to: any, from: any, next: any) {
          await initializeWorkspaces(next);
          const workspaceId = store.getters['workspace/selected'].id;
          store.dispatch('startLoadingPage');

          store
            .dispatch('transport-costs/read', { ...to.params, workspaceId })
            .then(() => {
              next();
            })
            .catch((err: ErrorResponse) => handleUnauthorized(err, next))
            .finally(() => {
              store.dispatch('stopLoadingPage');
            });
        },
      },
    },

    /*
        | -----------------
        | ERROR PAGES
        | -----------------
        | Show the general error pages
        */
    {
      path: '/errors/:code',
      name: 'error',
      component: loadView('errors/general'),
      meta: {
        title: 'Errors | Induline product manager',
        group: ['auth'],
        beforeResolve(to: any, from: any, next: any) {
          store.dispatch('startLoadingPage');

          next();

          store.dispatch('stopLoadingPage');
        },
      },
    },
  ],
});

router.beforeResolve(async (routeTo: any, routeFrom: any, next: any) => {
  try {
    if (routeFrom.meta && routeFrom.meta.leaveCallback) {
      await routeFrom.meta.leaveCallback(routeTo, routeFrom);
    }

    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.title) {
          document.title = route.meta.title;
        }
        if (route.meta && route.meta.group) {
          // @ts-ignore
          document.querySelector('body').classList.add(route.meta.group);
        }

        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args: any) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve({ success: true });
            }
          });
        } else {
          resolve({ success: true });
        }
      });
    }
  } catch (error) {
    return;
  }

  next();
});

/**
 * Guard guest middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
async function guardAuth(to: any, from: any, next: any) {
  try {
    const auth: Auth = await store.dispatch('user/me');

    if (to.meta.group && to.meta.group.includes('guest') && auth && auth.id) {
      return next({ name: 'products' });
    }

    if (to.meta.group && to.meta.group.includes('auth') && (!auth || !auth.id)) {
      return next({ name: 'login' });
    }

    if (to.meta.group && to.meta.group.includes('logout')) {
      await store.dispatch('reset');

      await store.dispatch('user/logout');

      return next({ name: 'login' });
    }

    next();
  } catch (e) {
    if (to.meta.group && to.meta.group.includes('guest') && !to.meta.group.includes('auth')) {
      return next();
    }

    next({ name: 'login' });
  }
}

async function initializeWorkspaces(next: any) {
  if (store.getters['workspace/all'].length > 0) {
    return;
  }
  const workspaces = await store.dispatch('workspace/index', { per_page: 20, s: { id: SortOrder.ASC } }).catch((err: ErrorResponse) => handleUnauthorized(err, next));
  if (workspaces.length === 0) {
    return;
  }
  const workspaceId = localStorage.getItem('selected_workspace_id');
  if (!workspaceId) {
    await store.dispatch('workspace/select', { workspace: workspaces[0] });
    return;
  }
  const parsedWorkspaceId = parseInt(workspaceId, 10);
  const workspace = workspaces.find((ws: Workspace) => ws.id === parsedWorkspaceId);
  await store.dispatch('workspace/select', { workspace });
}

function loadView(view: string) {
  return () => lazyLoadView(import(`@/views/${view}.vue`));
}

/**
 * Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * @param {AsyncView} The view component
 */
function lazyLoadView(AsyncView: any) {
  const AsyncHandler = () => ({
    component: AsyncView,
    delay: 400,
    timeout: 10000,
  });

  return Promise.resolve({
    functional: true,
    render(h: CreateElement, { data, children }: any) {
      return h(AsyncHandler, data, children);
    },
  });
}

function handleUnauthorized(error: ErrorResponse, next: any) {
  if (error.status === 401) {
    next({ name: 'error', params: { code: '401' } });
  }
}

export default router;
